/**
 * LeadSyncFormModal — Finance Partner Sync modal for OneButton dashboard.
 *
 * Lets users:
 * - Sync with an account id (search and link existing finance partner lead).
 * - View existing leads in a card list and select which to sync.
 * - Sync selected leads (resetSyncData) or create new finance partner leads.
 *
 * Types live in LeadSyncFormModal.types.ts; handlers are defined at top level
 * (no functions between DOM) per project coding standards.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Modal, notification } from 'antd';
import { X, CheckCircle, User, ArrowsClockwise, Warning, Table, SquaresFour } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import {
    isEnfin,
    isGoodLeap,
    isIgs,
    isLightreach,
    leadTypeMatchesSelectedProduct,
    lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts,
} from './productsFinanceHubHelpers';
import { findSyncStatusRowForProduct } from './partnerSyncConfig';
import quoteServices, { type LeadSyncPayload } from '../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import FinancePartnerCreateLeadProgressModal from './FinancePartnerCreateLeadProgressModal';
import FinancePartnerSyncConfirmModal, {
    leadMatchesConfirmLabel,
    partnerTitleFromConfirmLabels,
    uniqueFinancePartnerConfirmLabels,
} from './FinancePartnerSyncConfirmModal';
import type { LeadSyncFormModalProps, FinancePartnerLead } from './LeadSyncFormModal.types';
import DashboardModal from './DashboardModal';
import './oneButton-css/LeadSyncFormModal.css';
import { OBD_FINANCE_PARTNER_SYNC_Z } from './oneButtonOverlayZ';
import {
    getContractApiErrorMessage,
    getContractApiResultErrorMessage,
} from '../../../../helpers/contractApiErrorMessage';
import {
    invalidateRetrievedOpportunityDetails,
    listSyncedIgsAndLightreachLeads,
    withFinancePartnerLeadList,
    keepSyncedLeadsExceptProductCodes,
} from '../../../../helpers/lightReachLeadId';

export type { LeadSyncFormModalProps } from './LeadSyncFormModal.types';

/** Same partner, different spacing → one dropdown key. */
function normalizePartnerKey(name: string | null | undefined): string {
    return String(name ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

function isIgsOrLightreachPartner(name: string | null | undefined): boolean {
    return isIgs(name || '') || isLightreach(name || '');
}

const MISSING_SALESPERSON_MESSAGE =
    'No Trinity Salesperson has been assigned to this opportunity in Salesforce or the salesperson has not yet synced from Salesforce to oneBUTTON.';

function hasOpportunitySalesperson(opportunityDetails: any): boolean {
    const nested = opportunityDetails?.OpportunityDetails ?? opportunityDetails?.opportunityDetails;
    const salesPerson =
        opportunityDetails?.SalesPerson ??
        opportunityDetails?.salesPerson ??
        nested?.SalesPerson ??
        nested?.salesPerson;

    if (salesPerson) {
        return [
            salesPerson.Name,
            salesPerson.name,
            salesPerson.Email,
            salesPerson.email,
            salesPerson.Phone,
            salesPerson.phone,
            salesPerson.Sales_Division,
            salesPerson.sales_Division,
            salesPerson.Team,
            salesPerson.team,
        ].some((value) => String(value ?? '').trim() !== '');
    }

    return [
        opportunityDetails?.SalespersonName,
        opportunityDetails?.SalesPersonName,
        opportunityDetails?.sfdcTrinitySalesPerson,
        nested?.SalespersonName,
        nested?.SalesPersonName,
        nested?.sfdcTrinitySalesPerson,
    ].some((value) => String(value ?? '').trim() !== '');
}

function shouldBlockLightreachMissingSalesperson(
    partnerName: string | null | undefined,
    opportunityDetails: any,
): boolean {
    return isLightreach(partnerName || '') && !hasOpportunitySalesperson(opportunityDetails);
}

function bumpFinanceSelectionRefresh(): void {
    useOneButtonStore.getState().setFinanceSelectionRefreshTrigger(Date.now());
}

function isManualAccountSearchPartner(name: string | null | undefined): boolean {
    const n = String(name ?? '');
    return (
        isLightreach(n) ||
        isGoodLeap(n) ||
        normalizePartnerKey(n) === normalizePartnerKey(COMMON_CONSTANTS.SunlightPartnerName)
    );
}

/** V1 availableFinancePartners for Account Id search: Lightreach / GoodLeap on the deal (not IGS). */
function accountSearchPartnerOptions(opportunityDetails: any): { id: string; label: string }[] {
    const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const out: { id: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const p of products) {
        if (p?.ActiveFlag === false || p?.activeFlag === false) continue;
        const partner = String(
            p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? '',
        );
        if (!isManualAccountSearchPartner(partner)) continue;
        const id = String(p.Finance_Partner_Id ?? p.FinancePartnerId ?? '').trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
            id,
            label: isGoodLeap(partner) ? 'GoodLeap' : 'Lightreach',
        });
    }
    return out;
}
function partnerIdForLeadName(opportunityDetails: any, financePartnerName: string | null | undefined): string {
    const want = normalizePartnerKey(financePartnerName);
    if (!want) return '';
    const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    for (const p of products) {
        const name = String(
            p?.Finance_Partner_Display_Name ?? p?.FinancePartnerDisplayName ?? p?.Finance_Partner ?? '',
        );
        if (normalizePartnerKey(name) === want) {
            return String(p?.Finance_Partner_Id ?? p?.FinancePartnerId ?? '').trim();
        }
    }
    return '';
}

/** V1 sunlight-lead-model: Battery Only and Battery are the same product for CreateLead. */
function normalizeLeadProductKey(type: string | null | undefined): string {
    return String(type || '')
        .trim()
        .toLowerCase()
        .replace(/\s+only$/, '');
}

/** One selectable lead per finance partner + product (V1: no two Solar leads for the same FP). */
function leadSyncSelectionKey(lead: FinancePartnerLead): string {
    return `${normalizePartnerKey(lead.financePartnerName)}|${normalizeLeadProductKey(lead.lead_Type)}`;
}

function atMostOneLeadPerProductAndPartner(leads: FinancePartnerLead[]): FinancePartnerLead[] {
    const out: FinancePartnerLead[] = [];
    const seen = new Set<string>();
    for (const lead of leads) {
        const key = leadSyncSelectionKey(lead);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(lead);
    }
    return out;
}

function productCodeFromType(type: string | null | undefined): string {
    const t = normalizeLeadProductKey(type);
    if (t === 'battery') return 'B';
    if (t === 'roof') return 'R';
    if (t === 'rrr') return 'RRR';
    if (t === 'rr') return 'RR';
    if (t === 'solar') return 'S';
    return 'S';
}

function progressTitleFromProducts(
    products: { financePartner?: string }[],
    fallback = '',
): string {
    const labels = uniqueFinancePartnerConfirmLabels(products.map((p) => p.financePartner));
    if (labels.length === 1) return labels[0] === 'Lightreach' ? 'LightReach' : labels[0];
    if (labels.length > 1) return labels.join(', ');
    return fallback;
}

function productsFromSyncPayload(
    payload: LeadSyncPayload[],
    leads: FinancePartnerLead[] = [],
): { productType: string; category: string; financePartner?: string }[] {
    const out: { productType: string; category: string; financePartner?: string }[] = [];
    const seen = new Set<string>();
    for (const row of payload) {
        const type = String(row.productType || '').trim();
        if (!type) continue;
        const key = type.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const lead = leads.find((l) => String(l.lead_Type || '').trim().toLowerCase() === key);
        out.push({
            productType: type,
            category: String(row.product_Category || type),
            financePartner: lead?.financePartnerName,
        });
    }
    return out;
}

function pickLeadForPartner(
    rawLeads: FinancePartnerLead[],
    match: (name: string | null | undefined) => boolean,
): FinancePartnerLead | undefined {
    return (
        rawLeads.find((l) => match(l.financePartnerName) && l.IsCurrentLead) ||
        rawLeads.find((l) => match(l.financePartnerName))
    );
}

/** Mixed IGS + Lightreach: keep one lead selected per partner so they can be synced one after another. */
function initialSelectedLeadsForMixedDeal(
    rawLeads: FinancePartnerLead[],
    syncedLeads: FinancePartnerLead[],
): FinancePartnerLead[] {
    const mixed =
        rawLeads.some((l) => isIgs(l.financePartnerName)) &&
        rawLeads.some((l) => isLightreach(l.financePartnerName));
    if (!mixed) {
        const base = syncedLeads.length > 0 ? syncedLeads : rawLeads.length > 0 ? [rawLeads[0]] : [];
        return atMostOneLeadPerProductAndPartner(base);
    }
    const selected = [...syncedLeads];
    const addIfMissing = (match: (name: string | null | undefined) => boolean) => {
        if (selected.some((l) => match(l.financePartnerName))) return;
        const pick = pickLeadForPartner(rawLeads, match);
        if (pick) selected.push(pick);
    };
    addIfMissing(isIgs);
    addIfMissing(isLightreach);
    return atMostOneLeadPerProductAndPartner(selected);
}

/** V1 sunlight-lead-model parseAddress */
function parseAddress(Address: any) {
    if (Address == null || Address === '') return Address;
    let lastWord = String(Address).slice(String(Address).lastIndexOf(' ') + 1).toLowerCase();
    if (
        lastWord.includes('street') ||
        lastWord.includes('st') ||
        lastWord.includes('avenue') ||
        lastWord.includes('ave') ||
        lastWord.includes('road') ||
        lastWord.includes('rd') ||
        lastWord.includes('Trail') ||
        lastWord.includes('Trl') ||
        lastWord.includes('drive') ||
        lastWord.includes('dr') ||
        lastWord.includes('lane') ||
        lastWord.includes('ln') ||
        lastWord.includes('court') ||
        lastWord.includes('crt') ||
        lastWord.includes('ct') ||
        lastWord.includes('park') ||
        lastWord.includes('prk')
    ) {
        Address = String(Address).substring(0, String(Address).lastIndexOf(' '));
    }
    return Address;
}

/** V1 setFinancePartners rows: { product, financePartner } from opportunity products. */
function v1FinancePartnerPairs(opportunityDetails: any): { product: string; financePartner: string }[] {
    const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const fromStore = opportunityDetails?.financePartners;
    if (Array.isArray(fromStore) && fromStore.length > 0) {
        return fromStore.map((ele: any) => ({
            product: String(ele.product ?? ele.Product ?? ''),
            financePartner: String(ele.financePartner ?? ele.FinancePartner ?? ''),
        }));
    }
    return products.map((p: any) => ({
        product: String(p.Product_Type_Name ?? p.product_Type_Name ?? p.ProductType ?? ''),
        financePartner: String(
            p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? '',
        ),
    }));
}

/** V1 sunlight-lead-model getSelectedProductType */
function getSelectedProductType(opportunityDetails: any): string {
    const financePartners = v1FinancePartnerPairs(opportunityDetails);
    const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName, SunnovaPartnerName, IGSPartnerName } =
        COMMON_CONSTANTS;
    let selectedProductType = '';
    if (
        financePartners.findIndex(
            (ele) =>
                ele.product == SolarProductName &&
                (ele.financePartner == SunnovaPartnerName || ele.financePartner == IGSPartnerName),
        ) > -1
    )
        selectedProductType = 'S';
    if (
        financePartners.findIndex(
            (ele) => ele.product == RoofProductName && ele.financePartner == SunnovaPartnerName,
        ) > -1
    )
        selectedProductType = selectedProductType === 'S' ? 'SR' : 'RO';

    if (
        financePartners.findIndex(
            (ele) => ele.product == BatteryProductName && ele.financePartner == SunnovaPartnerName,
        ) > -1
    ) {
        if (selectedProductType === 'S') selectedProductType = selectedProductType === 'S' ? 'SB' : 'BO';
        if (selectedProductType === 'SR') selectedProductType = selectedProductType === 'SR' ? 'SBR' : 'BO';
        if (selectedProductType === '') selectedProductType = 'BO';
    }
    if (
        financePartners.findIndex(
            (ele) => ele.product == RRRProductName && ele.financePartner == SunnovaPartnerName,
        ) > -1
    ) {
        if (selectedProductType === 'S') selectedProductType = 'SRRR';
        if (selectedProductType === 'SB') selectedProductType = 'SBRRR';
        if (selectedProductType === 'BO') selectedProductType = 'BRRR';
        if (selectedProductType === '') selectedProductType = 'RRR';
    }
    return selectedProductType;
}

/** V1 sunlight-lead-model reSyncProductType */
function reSyncProductType(selectedLeads: FinancePartnerLead[], opportunityDetails: any): string | null {
    const financePartners = v1FinancePartnerPairs(opportunityDetails);
    const { SolarProductName, BatteryOnlyProductName, RoofOnlyProductName, RRRProductName, BatteryProductName } =
        COMMON_CONSTANTS;
    let type: any = null;
    if (selectedLeads.findIndex((ele) => ele.lead_Type == SolarProductName) > -1) type = 'S';
    if (selectedLeads.findIndex((ele) => ele.lead_Type == BatteryOnlyProductName) > -1)
        type = type == null ? 'B' : type + 'B';
    if (selectedLeads.findIndex((ele) => ele.lead_Type == RoofOnlyProductName) > -1)
        type = type == null ? 'R' : type + 'R';

    if (type == 'R') type = 'RO';
    if (type == 'B') type = 'BO';
    if (
        selectedLeads.findIndex((ele) => ele.lead_Type == RoofOnlyProductName) > -1 &&
        financePartners.findIndex((ele) => ele.product == RRRProductName) > -1
    )
        type = RRRProductName;
    if (
        selectedLeads.findIndex((ele) => ele.lead_Type == RoofOnlyProductName) > -1 &&
        financePartners.findIndex((ele) => ele.product == RRRProductName) > -1 &&
        selectedLeads.findIndex((ele) => ele.lead_Type == SolarProductName) > -1
    )
        type = 'SRRR';
    if (
        selectedLeads.findIndex((ele) => ele.lead_Type == RoofOnlyProductName) > -1 &&
        financePartners.findIndex((ele) => ele.product == RRRProductName) > -1 &&
        selectedLeads.findIndex((ele) => ele.lead_Type == BatteryProductName) > -1
    )
        type = 'BRRR';
    if (
        selectedLeads.findIndex((ele) => ele.lead_Type == RoofOnlyProductName) > -1 &&
        financePartners.findIndex((ele) => ele.product == RRRProductName) > -1 &&
        selectedLeads.findIndex(
            (ele: any) =>
                ele.lead_Type == BatteryOnlyProductName &&
                selectedLeads.findIndex((e) => e.lead_Type == SolarProductName) > -1,
        ) > -1
    )
        type = 'SBRRR';
    return type;
}

/** V1 sunlight-lead-model extractProduct */
function extractProduct(product: string) {
    switch (product) {
        case 'S':
            return 'Solar Only';
        case 'RO':
            return 'Roof Only';
        case 'SR':
            return 'Solar;Roof';
        case 'SB':
            return 'Solar;Battery';
        case 'SRB':
            return 'Solar;Roof;Battery';
        case 'RRR':
            return 'RRR';
        case 'SRRR':
            return 'Solar;RRR';
        default:
            return product;
    }
}

/** V2: Partner Sync opened from a product card/checklist — match lead type to that product only. */
function leadMatchesSyncProductContext(
    lead: FinancePartnerLead,
    productName: string | null | undefined,
    partnerName: string | null | undefined,
    opportunityDetails: any,
    allSelectedLeads: FinancePartnerLead[],
): boolean {
    if (productName?.trim()) {
        return leadTypeMatchesSelectedProduct(lead.lead_Type, productName);
    }
    const leadType = reSyncProductType(allSelectedLeads, opportunityDetails);
    const legacyCodes = getSelectedProductType(opportunityDetails);
    if (legacyCodes && legacyCodes.includes(String(leadType ?? ''))) return true;
    // Lightreach / GoodLeap deals: V1 combined codes stay empty — match lead to an active product on this partner.
    if (!legacyCodes && partnerName?.trim()) {
        const want = normalizePartnerKey(partnerName);
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        return products.some((p: any) => {
            if (p?.ActiveFlag === false || p?.activeFlag === false) return false;
            const partner = String(
                p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? '',
            );
            if (normalizePartnerKey(partner) !== want) return false;
            const pn = String(p.Product_Type_Name ?? p.product_Type_Name ?? p.ProductType ?? '');
            return leadTypeMatchesSelectedProduct(lead.lead_Type, pn);
        });
    }
    return false;
}

function displayAppProductForSyncError(
    productName: string | null | undefined,
    opportunityDetails: any,
): string {
    if (productName?.trim()) return productName.trim();
    return extractProduct(getSelectedProductType(opportunityDetails));
}

// ---------------------------------------------------------------------------
// LeadCard — single lead card (logic at top, no functions between DOM)
// ---------------------------------------------------------------------------

/**
 * Renders one lead as a selectable card: name, date, pills, address, sub-labels, selection icon.
 * All formatting and handlers are defined above the return so there are no functions between DOM.
 */
const LeadCard: React.FC<{
    lead: FinancePartnerLead;
    isSelected: boolean;
    disabled?: boolean;
    onSelect: (lead: FinancePartnerLead) => void;
}> = ({ lead, isSelected, disabled, onSelect }) => {
    // Formatted display values (computed once at top)
    const addressLine1 = lead.Street || '';
    const addressLine2 = [lead.City, lead.State].filter(Boolean).join(', ');
    const formattedDate = lead.LastModifiedDate
        ? dayjs(lead.LastModifiedDate.split('T')[0]).format('MMM D, YYYY')
        : null;

    const handleClick = () => {
        if (disabled) return;
        onSelect(lead);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(lead);
        }
    };

    return (
        <div
            className={`obd-lead-sync-form__card ${isSelected ? 'obd-lead-sync-form__card--selected' : ''} ${lead.IsCurrentLead ? 'obd-lead-sync-form__card--current' : ''}${disabled ? ' obd-lead-sync-form__card--disabled' : ''}`}
            onClick={handleClick}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onKeyDown={handleKeyDown}
        >
            {/* Name (left) and last modified date (right) */}
            <div className="obd-lead-sync-form__card-head">
                <h4 className="obd-lead-sync-form__card-name">
                    {lead.FirstName} {lead.LastName}
                </h4>
                {formattedDate && (
                    <span className="obd-lead-sync-form__card-date">{formattedDate}</span>
                )}
            </div>
            {/* Lead type and finance partner as pills */}
            <div className="obd-lead-sync-form__card-meta">
                <span className="obd-lead-sync-form__card-pill">{lead.lead_Type}</span>
                <span className="obd-lead-sync-form__card-pill">{lead.financePartnerName}</span>
            </div>
            {(addressLine1 || addressLine2) && (
                <p className="obd-lead-sync-form__card-address">
                    {addressLine1}
                    {addressLine1 && addressLine2 && <br />}
                    {addressLine2}
                </p>
            )}
            {/* Extended Details */}
            <div className="obd-lead-sync-form__card-body">
                <div className="obd-lead-sync-form__card-row">
                    <span className="obd-lead-sync-form__card-key">Lead ID</span>
                    <span className="obd-lead-sync-form__card-val">{lead.FinancePartnerSyncLeadId || '—'}</span>
                </div>
                {lead.SalespersonName && (
                    <div className="obd-lead-sync-form__card-row">
                        <span className="obd-lead-sync-form__card-key">Sales</span>
                        <span className="obd-lead-sync-form__card-val">{lead.SalespersonName}</span>
                    </div>
                )}

                {((lead as any).Status || (lead as any).MatchStatus) && (
                    <div className="obd-lead-sync-form__card-row">
                        <span className="obd-lead-sync-form__card-key">Match Status</span>
                        <span className="obd-lead-sync-form__card-val">{(lead as any).Status || (lead as any).MatchStatus}</span>
                    </div>
                )}
                <div className="obd-lead-sync-form__card-row" title={lead.TitleVerified ? 'Title verified' : 'Title not verified'}>
                    <span className="obd-lead-sync-form__card-key">Title</span>
                    <span className={`obd-lead-sync-form__card-val ${lead.TitleVerified ? 'obd-lead-sync-form__card-val--ok' : 'obd-lead-sync-form__card-val--err'}`}>
                        {lead.TitleVerified ? <CheckCircle size={16} weight="fill" /> : <X size={16} weight="bold" />}
                    </span>
                </div>
            </div>
            {/* Selection indicator (checkmark or empty circle) */}
            <div className="obd-lead-sync-form__card-select">
                {isSelected ? (
                    <CheckCircle size={22} weight="fill" className="obd-lead-sync-form__card-check obd-lead-sync-form__card-check--on" />
                ) : (
                    <div className="obd-lead-sync-form__card-check obd-lead-sync-form__card-check--off" aria-hidden />
                )}
            </div>
        </div>
    );
};

export const LeadSyncFormModal: React.FC<LeadSyncFormModalProps> = ({ open, onClose }) => {
    // ----- Store (OneButton global state) -----
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const theme = useOneButtonStore((s) => s.theme);
    const partnerName = useOneButtonStore((s) => s.leadSyncFormPartnerName);
    const productName = useOneButtonStore((s) => s.leadSyncFormProductName);
    const openAction = useOneButtonStore((s) => s.leadSyncFormAction);
    const hideCreateLead = useOneButtonStore((s) => s.leadSyncFormHideCreateLead);
    const setRawStatuses = useOneButtonStore((s) => s.setFinancePartnerRawSyncStatuses);

    // ----- Local state -----
    const [viewType, setViewType] = useState<'card' | 'table'>('table');
    const [accountId, setAccountId] = useState('');
    const [manualAccountSearch, setManualAccountSearch] = useState(false);
    const [financePartnerId, setFinancePartnerId] = useState('');
    const [syncLoading, setSyncLoading] = useState(false);
    const [leads, setLeads] = useState<FinancePartnerLead[]>([]);
    const [selectedLeads, setSelectedLeads] = useState<FinancePartnerLead[]>([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [createProgressOpen, setCreateProgressOpen] = useState(false);
    const [leadProgressAction, setLeadProgressAction] = useState<'create' | 'sync'>('create');
    const [leadSyncPayload, setLeadSyncPayload] = useState<LeadSyncPayload[] | undefined>();
    const [isCreatingInline, setIsCreatingInline] = useState(false);
    const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
    const [needsRefreshOnClose, setNeedsRefreshOnClose] = useState(false);
    const [fpConfirmOpen, setFpConfirmOpen] = useState(false);
    const [fpConfirmPartners, setFpConfirmPartners] = useState<string[]>([]);
    const [syncProgressProducts, setSyncProgressProducts] = useState<{ productType: string; category: string }[] | null>(null);
    const [syncProgressPartnerName, setSyncProgressPartnerName] = useState('');
    const [utilityRestoreProductTypes, setUtilityRestoreProductTypes] = useState<string[]>([]);

    const [noticeAPI, noticeContextHolder] = notification.useNotification();

    const opportunityPartnerFlags = useMemo(() => {
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        let hasIgs = false;
        let hasLightreach = false;
        let hasSunlight = false;
        let hasGoodLeap = false;
        let hasEnfin = false;
        const labels: string[] = [];
        const seen = new Set<string>();
        const order = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];
        const sorted = [...products].sort((a: any, b: any) => {
            const an = String(a?.Product_Type_Name ?? a?.product_Type_Name ?? '').trim();
            const bn = String(b?.Product_Type_Name ?? b?.product_Type_Name ?? '').trim();
            const ai = order.indexOf(an);
            const bi = order.indexOf(bn);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
        sorted.forEach((p: any) => {
            if (p?.ActiveFlag === false || p?.activeFlag === false) return;
            const partner = String(
                p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? '',
            );
            if (!partner || /select finance/i.test(partner) || /cash/i.test(partner)) return;
            if (isIgs(partner)) hasIgs = true;
            if (isLightreach(partner)) hasLightreach = true;
            if (normalizePartnerKey(partner) === normalizePartnerKey(COMMON_CONSTANTS.SunlightPartnerName)) hasSunlight = true;
            if (isGoodLeap(partner)) hasGoodLeap = true;
            if (isEnfin(partner)) hasEnfin = true;
            let label = partner;
            if (isLightreach(partner)) label = 'Lightreach';
            else if (isIgs(partner)) label = 'IGS';
            else if (isGoodLeap(partner)) label = 'GoodLeap';
            else if (isEnfin(partner)) label = 'Enfin';
            const key = label.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                labels.push(label);
            }
        });
        return { hasIgs, hasLightreach, hasSunlight, hasGoodLeap, hasEnfin, labels };
    }, [opportunityDetails?.OpportunityProduct, opportunityDetails?.opportunityProduct]);

    const leadSyncModalTitle = useMemo(() => {
        if (partnerName) return `Finance Partner Sync — ${partnerName}`;
        if (opportunityPartnerFlags.labels.length > 0) {
            return `Finance Partner Sync (${opportunityPartnerFlags.labels.join(', ')})`;
        }
        return 'Finance Partner Sync';
    }, [partnerName, opportunityPartnerFlags.labels]);

    // V1 Account Id search shows for Lightreach / Sunlight / GoodLeap — not IGS-only.
    const accountSearchPartners = useMemo(
        () => accountSearchPartnerOptions(opportunityDetails),
        [opportunityDetails?.OpportunityProduct, opportunityDetails?.opportunityProduct],
    );
    const showAccountIdSearch = accountSearchPartners.length > 0;

    const progressPartnerName = partnerName
        ? partnerName
        : opportunityPartnerFlags.labels.join(', ');

    const refreshOpportunity = useCallback(async () => {
        if (!opportunityID) return;
        const state = useOneButtonStore.getState();
        const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || opportunityID;
        invalidateRetrievedOpportunityDetails(opportunityID);
        const retrieved = await opportunityInfoServices.retrieveOpportunityDetails(String(opportunityID)).catch(() => null);
        const detail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
        const base = detail ?? state.opportunityDetails;
        const merged = withFinancePartnerLeadList(base, retrieved);
        if (merged) state.setOpportunityDetails(merged);
        else if (detail) state.setOpportunityDetails(detail);
    }, [opportunityID]);

    const fetchLeads = useCallback(async () => {
        if (!opportunityID) return;
        setLeadsLoading(true);
        try {
            const res = await opportunityInfoServices.getFinancePartnerLeads(opportunityID);
            let rawLeads: FinancePartnerLead[] = [];
            if (Array.isArray(res)) rawLeads = res;
            else if (res?.Leads && Array.isArray(res.Leads)) rawLeads = res.Leads;
            else if (res?.data && Array.isArray(res.data)) rawLeads = res.data;

            if (partnerName) {
                const pName = partnerName.toUpperCase();
                rawLeads = rawLeads.filter(l => (l.financePartnerName || '').toUpperCase() === pName);
            }
            if (productName) {
                rawLeads = rawLeads.filter((l) => leadTypeMatchesSelectedProduct(l.lead_Type, productName));
            }

            setLeads(rawLeads);
            const oppDetailLocal = useOneButtonStore.getState().opportunityDetails;
            const list = oppDetailLocal?.financePartnerLeadList ?? oppDetailLocal?.FinancePartnerLeadList ?? [];
            const idSet = new Set(
                Array.isArray(list)
                    ? list.map((x: any) => (typeof x === 'string' ? x : x?.Finance_Partner_Sync_LeadID ?? x?.FinancePartnerSyncLeadId)).filter(Boolean)
                    : []
            );
            const syncedLeads = rawLeads.filter(
                (l) => l.IsCurrentLead || idSet.has(l.FinancePartnerSyncLeadId),
            );
            setSelectedLeads(initialSelectedLeadsForMixedDeal(rawLeads, syncedLeads));
            // V1 financePartnerAccountId starts '' — do not copy current-lead IDs into Account Id on load.
        } catch (err) {
            console.error("Failed to fetch leads", err);
            setLeads([]);
        } finally {
            setLeadsLoading(false);
        }
    }, [opportunityID, partnerName, productName]);

    // ----- Effect: fetch leads when modal opens -----
    useEffect(() => {
        if (open && opportunityID) {
            setAccountId('');
            setManualAccountSearch(false);
            setFinancePartnerId(
                accountSearchPartners.length === 1 ? accountSearchPartners[0].id : '',
            );
            setMessage(null);
            fetchLeads();
        }
    }, [open, opportunityID, fetchLeads]);

    // ----- Handlers: sync with lead id (V1 financePartnerAccountSearch + address confirm) -----
    const handleSyncWithAccountId = async (leadsForSync: FinancePartnerLead[] = selectedLeads) => {
        if (!opportunityID) return;
        const typedId = accountId?.trim() ?? '';
        let searches: { accountId: string; partnerId: string; lead?: FinancePartnerLead }[] = [];

        if (manualAccountSearch) {
            if (!typedId || !financePartnerId) return;
            searches = [{ accountId: typedId, partnerId: String(financePartnerId) }];
        } else {
            // FinancePartnerLeadSearch 400s on IGS lead IDs (e.g. 26RI-…). Only search LR / Sunlight / GoodLeap.
            const accountSearchLead = leadsForSync.find((l) => isManualAccountSearchPartner(l.financePartnerName));
            const searchAccountId = String(accountSearchLead?.FinancePartnerSyncLeadId || '').trim();
            if (accountSearchLead && searchAccountId) {
                searches = [
                    {
                        accountId: searchAccountId,
                        partnerId: partnerIdForLeadName(opportunityDetails, accountSearchLead?.financePartnerName),
                        lead: accountSearchLead,
                    },
                ];
            }
        }
        const valid = searches.filter((s) => s.accountId);
        // IGS-only (prompt unchecked Lightreach): skip search and sync the remaining lead(s) directly — V1 IGS "Sync".
        const skipLeadSearch = !manualAccountSearch && valid.length === 0 && leadsForSync.length > 0;
        if (valid.length === 0 && !skipLeadSearch) return;

        const selectedPartnerLabel = manualAccountSearch
            ? accountSearchPartners.find((p) => p.id === financePartnerId)?.label
            : valid[0]?.lead?.financePartnerName;
        if (
            isLightreach(selectedPartnerLabel) &&
            shouldBlockLightreachMissingSalesperson('LightReach', opportunityDetails)
        ) {
            setMessage({ type: 'error', text: MISSING_SALESPERSON_MESSAGE });
            return;
        }

        const runLeadIdSearchAndProgress = async () => {
            setSyncLoading(true);
            setMessage(null);
            try {
                const statusSnapshot =
                    useOneButtonStore.getState().financePartnerRawSyncStatuses ?? [];
                const leadProductTypes = [
                    ...new Set(
                        (leadsForSync.length > 0 ? leadsForSync : selectedLeads).map((l) =>
                            String(l.lead_Type ?? 'Solar').trim(),
                        ),
                    ),
                ];
                setUtilityRestoreProductTypes(
                    leadProductTypes.filter((pt) => {
                        const row = findSyncStatusRowForProduct(statusSnapshot, pt);
                        return row?.IsUtility === true;
                    }),
                );

                let lastSuccessMsg = '';
                if (!skipLeadSearch) {
                    for (const s of valid) {
                        const res = await quoteServices.financePartnerLeadSearch(
                            String(opportunityID),
                            s.accountId,
                            s.partnerId,
                        );
                        const isSuccess =
                            res?.IsSuccessful === true ||
                            res?.IsSuccessful === 'true' ||
                            res?.IsSuccessful === 'True' ||
                            (typeof res === 'string' && res.toLowerCase().includes('successfully')) ||
                            (res?.Message && res.Message.toLowerCase().includes('successfully'));
                        if (!isSuccess) {
                            let msg = 'Account search request failed.';
                            const resultStr = res?.Message || JSON.stringify(res);
                            try {
                                const parsed = JSON.parse(resultStr);
                                msg = parsed?.message || parsed?.Message || msg;
                            } catch (e) {
                                msg = resultStr || msg;
                            }
                            setMessage({ type: 'error', text: msg });
                            return;
                        }
                        lastSuccessMsg = res?.Message || `Successfully synced and linked with Account ID: ${s.accountId}`;
                    }
                }

                setNeedsRefreshOnClose(true);
                if (
                    isIgsOrLightreachPartner(partnerName) ||
                    opportunityPartnerFlags.hasIgs ||
                    opportunityPartnerFlags.hasLightreach
                ) {
                    bumpFinanceSelectionRefresh();
                }
                await quoteServices.contractGetLoanStatusByOpportunity(String(opportunityID) as string | number);
                await refreshOpportunity();
                let payload: LeadSyncPayload[] = [];
                if (manualAccountSearch) {
                    const products =
                        opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
                    const partnerProduct = products.find(
                        (p: any) => String(p?.Finance_Partner_Id ?? p?.FinancePartnerId ?? '') === String(financePartnerId),
                    );
                    const productType = String(
                        partnerProduct?.Product_Type_Name ??
                            partnerProduct?.product_Type_Name ??
                            partnerProduct?.ProductType ??
                            '',
                    );
                    payload = valid.map((s) => ({
                        finance_Partner_Lead_Sync_Id: s.accountId,
                        productType,
                        address: true,
                        title: true,
                        product_Category: productCodeFromType(productType),
                    }));
                } else {
                    const payloadSource =
                        leadsForSync.length > 0 ? leadsForSync : valid.map((s) => s.lead).filter(Boolean);
                    payload = payloadSource.map((l: any) => {
                        const productType = String(l.lead_Type ?? l.Lead_Type ?? 'Solar').trim();
                        return {
                            finance_Partner_Lead_Sync_Id: l.FinancePartnerSyncLeadId ?? l.accountId,
                            productType,
                            // V1 sunlight-lead-model sync(): use lead flags; default false to avoid wiping automation sync.
                            address: l.AddressValidated === true || l.AddressValidated === 'true',
                            title: l.TitleVerified === true || l.TitleVerified === 'true',
                            product_Category: productCodeFromType(productType),
                        };
                    });
                }
                setLeadProgressAction('sync');
                setLeadSyncPayload(payload);
                setSyncProgressProducts(productsFromSyncPayload(payload, manualAccountSearch ? [] : leadsForSync));
                setSyncProgressPartnerName(
                    partnerTitleFromConfirmLabels(
                        uniqueFinancePartnerConfirmLabels(
                            (manualAccountSearch
                                ? [selectedPartnerLabel]
                                : leadsForSync.map((l) => l.financePartnerName)
                            ),
                        ),
                    ),
                );
                setCreateProgressOpen(true);
                if (lastSuccessMsg) {
                    setMessage({ type: 'success', text: lastSuccessMsg });
                }
            } catch (err: unknown) {
                setMessage({
                    type: 'error',
                    text: getContractApiErrorMessage(err, 'An error occurred in account search request.'),
                });
            } finally {
                setSyncLoading(false);
            }
        };

        if (manualAccountSearch) {
            await runLeadIdSearchAndProgress();
            return;
        }

        // V1 sunlight-lead-model syncLead — compare InstallationAddress to each selected lead, then confirm or sync.
        let syncSuccessCount = 0;
        let addressMatchCount = 0;
        let leadTypeMatchCount = 0;
        let leadType: any;
        const nested = opportunityDetails?.OpportunityDetails ?? opportunityDetails?.opportunityDetails;
        const installationAddress =
            opportunityDetails?.InstallationAddress ??
            opportunityDetails?.installationAddress ??
            nested?.InstallationAddress ??
            nested?.installationAddress ??
            {};
        leadsForSync.forEach((ele: any) => {
            leadType = reSyncProductType(leadsForSync, opportunityDetails);
            if (leadMatchesSyncProductContext(ele, productName, partnerName, opportunityDetails, leadsForSync)) {
                const leadAddress = ele.Address ?? ele.Street;
                const extractedAddress = parseAddress(leadAddress);
                if (
                    installationAddress?.City?.toLowerCase().includes(String(ele.City || '').toLowerCase()) &&
                    installationAddress?.Street?.toLowerCase().includes(String(extractedAddress ?? '').toLowerCase()) &&
                    installationAddress?.stateCode?.toLowerCase().includes(String(ele.State || '').toLowerCase())
                ) {
                    syncSuccessCount++;
                } else addressMatchCount++;
            } else leadTypeMatchCount++;
        });

        const syncingPartialMixedFp =
            opportunityPartnerFlags.hasIgs &&
            opportunityPartnerFlags.hasLightreach &&
            uniqueFinancePartnerConfirmLabels(leadsForSync.map((l) => l.financePartnerName)).length < 2;

        if (syncSuccessCount > 0 && syncSuccessCount == leadsForSync.length) {
            await runLeadIdSearchAndProgress();
        } else if (leadTypeMatchCount > 0 && leadType != 'RO' && !syncingPartialMixedFp) {
            const product = displayAppProductForSyncError(productName, opportunityDetails);
            const leadTypeLabel =
                leadsForSync[0]?.lead_Type ??
                (leadType != null && leadType !== '' ? String(leadType) : '');
            setMessage({
                type: 'error',
                text:
                    'Selected product type in oneBUTTON ' +
                    (product ? `(${product}) ` : '') +
                    'does not match the lead type of ' +
                    leadTypeLabel +
                    '. Please update the product type in oneBUTTON or find the correct lead type to sync with in Finance Partner.',
            });
        } else if (leadTypeMatchCount == 0 && addressMatchCount > 0) {
            Modal.confirm({
                title: '',
                content: 'Installation address and lead address does not match. Do you want to continue sync?',
                okText: 'OK',
                cancelText: 'Cancel',
                centered: true,
                zIndex: OBD_FINANCE_PARTNER_SYNC_Z + 20,
                onOk: () => runLeadIdSearchAndProgress(),
            });
        } else {
            await runLeadIdSearchAndProgress();
        }
    };

    const handleSyncButtonClick = () => {
        if (manualAccountSearch) {
            void handleSyncWithAccountId();
            return;
        }
        const fps = uniqueFinancePartnerConfirmLabels(selectedLeads.map((l) => l.financePartnerName));
        if (fps.length < 2) {
            void handleSyncWithAccountId();
            return;
        }
        setFpConfirmPartners(fps);
        setFpConfirmOpen(true);
    };

    const handleFpConfirmSync = (checked: string[]) => {
        if (checked.length === 0) return;
        const filtered = selectedLeads.filter((l) =>
            checked.some((label) => leadMatchesConfirmLabel(l.financePartnerName, label)),
        );
        if (filtered.length === 0) return;
        setFpConfirmOpen(false);
        void handleSyncWithAccountId(filtered);
    };

    const handleLeadSelect = (lead: FinancePartnerLead) => {
        if (manualAccountSearch) return;
        setSelectedLeads((current) => {
            const isSelected = current.some((l) => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId);
            if (isSelected) {
                return current.filter((l) => l.FinancePartnerSyncLeadId !== lead.FinancePartnerSyncLeadId);
            }
            const key = leadSyncSelectionKey(lead);
            const withoutSameProduct = current.filter((l) => leadSyncSelectionKey(l) !== key);
            return [...withoutSameProduct, lead];
        });
    };

    const getProduct = (product: string): string | undefined => {
        const t = String(product || '').trim().toLowerCase().replace(/\s+only$/, '');
        if (t === 'battery') return 'B';
        if (t === 'roof') return 'R';
        if (t === 'rrr') return 'RRR';
        if (t === 'rr') return 'RR';
        if (t === 'solar') return 'S';
        return undefined;
    };

    /** Same rule as FinancePartnerCreateLeadProgressModal: only blank or IsSuccessful false is a failure. */
    const isCreateLeadSuccess = (res: unknown): boolean => {
        if (res === '' || res == null) return false;
        if (!res || typeof res !== 'object') return res !== false && res !== 'false';
        const row = res as { IsSuccessful?: unknown };
        return row.IsSuccessful !== false && row.IsSuccessful !== 'false';
    };

    // ----- Handlers: Create Lead inline for Lightreach -----
    const handleCreateLightreachLead = async () => {
        if (!opportunityID) return;
        if (shouldBlockLightreachMissingSalesperson(partnerName || 'LightReach', opportunityDetails)) {
            setMessage({ type: 'error', text: MISSING_SALESPERSON_MESSAGE });
            return;
        }
        setIsCreatingInline(true);
        setMessage(null);
        try {
            const products: any[] =
                opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
            const LRProducts = products.filter((ele) => {
                const partner = String(
                    ele?.Finance_Partner_Display_Name ??
                        ele?.FinancePartnerDisplayName ??
                        ele?.Finance_Partner ??
                        '',
                );
                const active = ele?.ActiveFlag !== false && ele?.activeFlag !== false;
                return active && isLightreach(partner);
            });

            let LeadForProduct = [
                ...new Set(
                    LRProducts.map((p) =>
                        getProduct(
                            String(
                                p.Product_Type_Name ??
                                    p.product_Type_Name ??
                                    p.ProductTypeName ??
                                    '',
                            ),
                        ),
                    ).filter((code): code is string => !!code),
                ),
            ];
            if (lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts(products)) {
                LeadForProduct = LeadForProduct.filter((code) => code !== 'B');
            }
            // Partner Sync is per product. Creating a Battery lead must not reset Solar (and the reverse).
            const openedForCode = productName?.trim() ? getProduct(productName) : undefined;
            if (openedForCode && LeadForProduct.includes(openedForCode)) {
                LeadForProduct = [openedForCode];
            }

            if (LeadForProduct.length === 0) {
                setMessage({ type: 'error', text: 'No Lightreach product found to create a lead.' });
                return;
            }

            // V1 sunlight-lead-model updateConfirmationStataus: ResetSyncData is [] for a Solar
            // create, or only the other Lightreach Solar/Battery lead. Roof is not part of this call.
            const resetPayload = keepSyncedLeadsExceptProductCodes(
                opportunityDetails,
                new Set(LeadForProduct),
            ).filter((row) => row.product_Category === 'S' || row.product_Category === 'B');
            const resetRes = await quoteServices.resetSyncData(String(opportunityID), resetPayload);
            if (resetRes !== 'Success' && resetRes !== 'success') {
                setMessage({
                    type: 'error',
                    text: getContractApiResultErrorMessage(resetRes, 'Sync not happened'),
                });
                return;
            }

            for (const productCode of LeadForProduct) {
                const res = await quoteServices.createLead({
                    opportunityId: String(opportunityID),
                    productSelection: {
                        Product_Category: productCode,
                        LeadForProduct: productCode,
                    },
                });
                if (!isCreateLeadSuccess(res)) {
                    const msg = getContractApiResultErrorMessage(
                        res,
                        `Some error occurred in Create lead request for ${productCode}`,
                    );
                    setMessage({ type: 'error', text: msg });
                    return;
                }
            }

            bumpFinanceSelectionRefresh();

            // Refresh raw sync statuses in store so checklist updates
            const stats = await quoteServices.getSyncStatusList(String(opportunityID));
            const list = stats?.FinancePartnerSyncStatuses || stats || [];
            if (Array.isArray(list)) setRawStatuses(list);

            // Refresh the cards list and opportunity state
            await refreshOpportunity();
            await fetchLeads();

            // Lightreach getLoanStatus
            await quoteServices.contractGetLoanStatusByOpportunity(String(opportunityID) as string | number);
        } catch (err: unknown) {
            setMessage({
                type: 'error',
                text: getContractApiErrorMessage(err, 'Failed to create lead'),
            });
        } finally {
            setIsCreatingInline(false);
        }
    };

    // ----- Derived: can submit account sync; product list for Create New Lead modal -----
    const handleManualAccountSearchToggle = (checked: boolean) => {
        setManualAccountSearch(checked);
        if (checked) {
            setSelectedLeads([]);
            setAccountId('');
            setFinancePartnerId(accountSearchPartners.length === 1 ? accountSearchPartners[0].id : '');
        }
    };

    const canSyncAccount = manualAccountSearch
        ? !!accountId.trim() && !!financePartnerId && !!opportunityID
        : selectedLeads.length > 0 && !!opportunityID;
    const { allPartnerProductsForCreate, productsMissingLeads } = useMemo(() => {
        const all = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const wantPartner = normalizePartnerKey(partnerName);
        const mixedIgsLightreach = opportunityPartnerFlags.hasIgs && opportunityPartnerFlags.hasLightreach;
        const syncedKeys = new Set(
            listSyncedIgsAndLightreachLeads(opportunityDetails).map((row) =>
                normalizeLeadProductKey(row.productType),
            ),
        );
        const allPartnerProductsForCreate: { productType: string; category: string; financePartner?: string }[] = [];
        const productsMissingLeads: { productType: string; category: string; financePartner?: string }[] = [];
        all.forEach((p: any) => {
            const partner = String(
                p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? '',
            );
            // GoodLeap / Enfin leads are created at agreement submit — never in this early create path.
            if (isGoodLeap(partner) || isEnfin(partner)) return;
            if (
                wantPartner &&
                !mixedIgsLightreach &&
                !(
                    (isLightreach(partnerName || '') && isLightreach(partner)) ||
                    (isIgs(partnerName || '') && isIgs(partner)) ||
                    normalizePartnerKey(partner) === wantPartner
                )
            ) {
                return;
            }
            const active = p.ActiveFlag !== false && p.activeFlag !== false;
            if (!active) return;
            const type = String(
                p.Product_Type_Name ?? p.product_Type_Name ?? p.ProductTypeName ?? p.ProductCode ?? p.ProductType ?? '',
            ).trim();
            if (!type) return;
            const category = p.ProductCategory || p.Product_Category || type;
            if (allPartnerProductsForCreate.some((o) => o.productType === type)) return;
            const item = { productType: type, category, financePartner: partner };
            allPartnerProductsForCreate.push(item);
            if (!syncedKeys.has(normalizeLeadProductKey(type))) {
                productsMissingLeads.push(item);
            }
        });
        const singleLrSolarBatteryLead = lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts(all);
        const stripBatteryFromCreateList = (list: typeof allPartnerProductsForCreate) =>
            singleLrSolarBatteryLead
                ? list.filter((item) => normalizeLeadProductKey(item.productType) !== 'battery')
                : list;
        return {
            allPartnerProductsForCreate: stripBatteryFromCreateList(allPartnerProductsForCreate),
            productsMissingLeads: stripBatteryFromCreateList(productsMissingLeads),
        };
    }, [opportunityDetails, partnerName, opportunityPartnerFlags.hasIgs, opportunityPartnerFlags.hasLightreach]);

    const productsForCreate =
        productsMissingLeads.length > 0 ? productsMissingLeads : allPartnerProductsForCreate;

    // ----- Handlers: form inputs and modals (no inline functions in JSX) -----
    const handleAccountIdChange = (e: React.ChangeEvent<HTMLInputElement>) => setAccountId(e.target.value);
    
    const handleOpenCreateProgress = () => {
        if (
            (opportunityPartnerFlags.hasLightreach || isLightreach(partnerName || '')) &&
            shouldBlockLightreachMissingSalesperson('LightReach', opportunityDetails)
        ) {
            setMessage({ type: 'error', text: MISSING_SALESPERSON_MESSAGE });
            return;
        }
        const productsToCreate = productsForCreate;
        if (productsToCreate.length === 0) {
            setMessage({ type: 'error', text: 'No product found to create a lead.' });
            return;
        }
        const keepLeads = keepSyncedLeadsExceptProductCodes(
            opportunityDetails,
            new Set(productsToCreate.map((product) => productCodeFromType(product.productType))),
        );
        if (isLightreach(partnerName || '') && !opportunityPartnerFlags.hasIgs) {
            handleCreateLightreachLead();
        } else if (!opportunityPartnerFlags.hasIgs && opportunityPartnerFlags.hasLightreach) {
            handleCreateLightreachLead();
        } else {
            setLeadProgressAction('create');
            setLeadSyncPayload(keepLeads);
            setSyncProgressProducts(productsToCreate);
            setSyncProgressPartnerName(progressTitleFromProducts(productsToCreate, progressPartnerName));
            setCreateProgressOpen(true);
        }
    };

    /** Close create-lead modal and refetch leads so the list stays in sync. */
    const handleCreateProgressClose = useCallback(async () => {
        setCreateProgressOpen(false);
        setSyncProgressProducts(null);
        setSyncProgressPartnerName('');
        setUtilityRestoreProductTypes([]);
        bumpFinanceSelectionRefresh();
        setNeedsRefreshOnClose(true);
        setLeadsLoading(true);
        try {
            await refreshOpportunity();
            await fetchLeads();
            // Refresh the overall sync status so the checklist updates
            if (opportunityID) {
                const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                const list = stats?.FinancePartnerSyncStatuses || stats || [];
                if (Array.isArray(list)) useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(list);
            }
        } finally {
            setLeadsLoading(false);
        }
    }, [fetchLeads, opportunityID, refreshOpportunity]);

    const setIsBackgroundSyncing = useOneButtonStore((s) => s.setIsBackgroundSyncing);

    /** Trigger full sync data updates on close if an action was taken */
    const handleCloseModal = async () => {
        setFpConfirmOpen(false);
        onClose(); // Hide instantly so underlying UI shows loading state
        if (needsRefreshOnClose && opportunityID) {
            setIsBackgroundSyncing(true);
            try {
                const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                const list = stats?.FinancePartnerSyncStatuses || stats || [];
                if (Array.isArray(list)) setRawStatuses(list);

                await refreshOpportunity();
            } catch (err) {
                console.error("Failed to refresh on close", err);
            } finally {
                setIsBackgroundSyncing(false);
            }
        }
    };

    // ----- Render -----
    return (
        <>
            <DashboardModal
                open={open}
                onClose={handleCloseModal}
                footer={null}
                title={leadSyncModalTitle}
                width="95%"
                maskClosable={false}
                zIndex={OBD_FINANCE_PARTNER_SYNC_Z}
                theme={theme}
                className={`obd-sync-modal obd-lead-sync-form-modal obd-theme-${theme}`}
                rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
                destroyOnClose
                loading={leadsLoading || syncLoading || isCreatingInline}
                loadingMessage={isCreatingInline ? 'Creating Lead...' : syncLoading ? 'Processing...' : 'Loading leads...'}
            >
                {noticeContextHolder}
                <div className="obd-sync-modal__inner obd-lead-sync-form__inner" style={{ position: 'relative' }}>
                    <div className="obd-lead-sync-form__body">

                        {/* Leads section: scrollable grid or table (if any) */}
                        <div className="obd-lead-sync-form__leads-section">
                            <div className="obd-lead-sync-form__leads-header">
                                    <h4 className="obd-lead-sync-form__leads-title">LEADS</h4>
                                    <div className="obd-quotes-view-toggle">
                                        <button
                                            type="button"
                                            className={`obd-quotes-view-btn ${viewType === 'card' ? 'obd-quotes-view-btn--active' : ''}`}
                                            onClick={() => setViewType('card')}
                                            title="Card View"
                                        >
                                            <SquaresFour size={16} weight="regular" /><span>Card</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`obd-quotes-view-btn ${viewType === 'table' ? 'obd-quotes-view-btn--active' : ''}`}
                                            onClick={() => setViewType('table')}
                                            title="Table View"
                                        >
                                            <Table size={16} weight="regular" /><span>Table</span>
                                        </button>
                                    </div>
                                </div>

                                {leads.length === 0 || viewType === 'table' ? (
                                    <div className="obd-lead-sync-form__table-container">
                                        <table className="obd-lead-sync-form__table">
                                            <thead>
                                                <tr>
                                                    <th className="obd-lead-sync-form__th">Select Lead</th>
                                                    <th className="obd-lead-sync-form__th">Lead ID</th>
                                                    <th className="obd-lead-sync-form__th">Lead Type</th>
                                                    <th className="obd-lead-sync-form__th">Finance Partner</th>
                                                    <th className="obd-lead-sync-form__th">First Name</th>
                                                    <th className="obd-lead-sync-form__th">Last Name</th>
                                                    <th className="obd-lead-sync-form__th">Address</th>
                                                    <th className="obd-lead-sync-form__th">City</th>
                                                    <th className="obd-lead-sync-form__th">State</th>
                                                    <th className="obd-lead-sync-form__th">Sales Person</th>
                                                    <th className="obd-lead-sync-form__th">Title Verified</th>
                                                    <th className="obd-lead-sync-form__th">Days Since Credit Run</th>
                                                    <th className="obd-lead-sync-form__th">Last Modified</th>
                                                    <th className="obd-lead-sync-form__th">Match Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leads.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={14} className="obd-lead-sync-form__empty" style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                                                            <div>Lead not found.</div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                leads.map((lead) => (
                                                    <tr key={lead.FinancePartnerSyncLeadId} className={lead.IsCurrentLead ? 'active-lead' : ''}>
                                                        <td className="obd-lead-sync-form__td">
                                                            <input 
                                                                type="checkbox" 
                                                                className="obd-lead-sync-form__checkbox"
                                                                checked={selectedLeads.some(l => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId)}
                                                                onChange={() => handleLeadSelect(lead)}
                                                                disabled={manualAccountSearch}
                                                            />
                                                        </td>
                                                        <td className="obd-lead-sync-form__td">{lead.FinancePartnerSyncLeadId}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.lead_Type}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.financePartnerName}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.FirstName}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.LastName}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.Street}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.City}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.State}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.SalespersonName}</td>
                                                        <td className="obd-lead-sync-form__td">
                                                            {lead.TitleVerified ? (
                                                                <CheckCircle size={18} weight="fill" className="obd-lead-sync-form__status-ok" />
                                                            ) : (
                                                                <X size={18} weight="bold" className="obd-lead-sync-form__status-err" />
                                                            )}
                                                        </td>
                                                        <td className="obd-lead-sync-form__td">{lead.DaysSinceCreditRun ?? '0'}</td>
                                                        <td className="obd-lead-sync-form__td">
                                                            {lead.LastModifiedDate ? dayjs(lead.LastModifiedDate.split('T')[0]).format('MM-DD-YYYY') : '-'}
                                                        </td>
                                                        <td className="obd-lead-sync-form__td">{(lead as any).Status || (lead as any).MatchStatus || '-'}</td>
                                                    </tr>
                                                ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="obd-lead-sync-form__leads-grid">
                                        {leads.map((lead) => (
                                            <LeadCard
                                                key={lead.FinancePartnerSyncLeadId}
                                                lead={lead}
                                                isSelected={selectedLeads.some(
                                                    (l) => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId
                                                )}
                                                disabled={manualAccountSearch}
                                                onSelect={handleLeadSelect}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>                        
                    </div>

                    {/* Footer: message (if any) + Sync existing lead / Create New Lead / Close */}
                    <div className="obd-sync-modal__footer obd-lead-sync-form__footer">
                        {message && message.type === 'error' && (
                            <div
                                className="obd-lead-sync-form__message obd-lead-sync-form__message--error"
                                style={{ flexBasis: '100%', order: 3, marginRight: 'auto', marginBottom: 0 }}
                            >
                                <Warning size={16} weight="fill" style={{ marginRight: 6 }} />
                                {message.text}
                            </div>
                        )}
                        {message && message.type === 'success' && (
                            <div
                                className="obd-lead-sync-form__message obd-lead-sync-form__message--success"
                                style={{ flexBasis: '100%', order: 3, marginRight: 'auto', marginBottom: 0 }}
                            >
                                <CheckCircle size={16} weight="fill" style={{ marginRight: 6 }} />
                                {message.text}
                            </div>
                        )}
                        {/* Sync with account id: input + finance partner dropdown + sync button (hidden for IGS) */}
                        {showAccountIdSearch && (
                        <div className="obd-lead-sync-form__sync-row" style={{ marginBottom: 0 }}>
                            <input
                                type="checkbox"
                                className="obd-lead-sync-form__manual-check"
                                checked={manualAccountSearch}
                                onChange={(e) => handleManualAccountSearchToggle(e.target.checked)}
                                aria-label="Enter Account Id from finance partner portal"
                            />
                            <input
                                type="text"
                                className="obd-lead-sync-form__input ant-input"
                                placeholder="Account Id"
                                value={accountId}
                                onChange={handleAccountIdChange}
                                disabled={!manualAccountSearch}
                                aria-label="Account Id"
                            />
                            {manualAccountSearch && (
                            <select
                                className="obd-lead-sync-form__select"
                                value={financePartnerId}
                                onChange={(e) => setFinancePartnerId(e.target.value)}
                                aria-label="Choose Finance Partner"
                            >
                                <option value="" disabled>
                                    Choose Finance Partner
                                </option>
                                {accountSearchPartners.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                            )}
                            <button
                                type="button"
                                className="obd-sync-modal__btn obd-sync-modal__btn--primary obd-lead-sync-form__sync-btn"
                                onClick={handleSyncButtonClick}
                                disabled={!canSyncAccount || syncLoading}
                                aria-label={manualAccountSearch ? 'Sync with Account Id' : 'Sync with Lead ID'}
                            >
                                <ArrowsClockwise size={18} weight="bold" />
                                {manualAccountSearch ? 'Sync with Account Id' : 'Sync with Lead ID'}
                            </button>
                        </div>
                        )}
                        <div className="obd-lead-sync-form__footer-btns">
                            {!hideCreateLead && (
                                <button
                                    type="button"
                                    className="obd-sync-modal__btn obd-sync-modal__btn--primary obd-lead-sync-form__create-new-btn"
                                    onClick={handleOpenCreateProgress}
                                    disabled={syncLoading || isCreatingInline || !opportunityID || selectedLeads.length > 1}
                                    aria-label="Create a new lead"
                                >
                                    <User size={18} weight="duotone" aria-hidden />
                                    Create a new lead
                                </button>
                            )}
                            <button type="button" className="obd-sync-modal__btn obd-sync-modal__btn--secondary" onClick={handleCloseModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </DashboardModal>

            <FinancePartnerSyncConfirmModal
                open={fpConfirmOpen}
                partners={fpConfirmPartners}
                theme={theme}
                sourceLabel="Lead ID"
                onCancel={() => setFpConfirmOpen(false)}
                onConfirm={handleFpConfirmSync}
            />

            {/* Create New Lead progress modal (opens from footer button) */}
            {createProgressOpen && opportunityID && (
                <FinancePartnerCreateLeadProgressModal
                    open={createProgressOpen}
                    onClose={handleCreateProgressClose}
                    opportunityId={opportunityID}
                    partnerName={syncProgressPartnerName || progressPartnerName}
                    products={syncProgressProducts ?? productsForCreate}
                    action={leadProgressAction}
                    syncPayload={leadSyncPayload}
                    utilityRestoreProductTypes={utilityRestoreProductTypes}
                />
            )}
        </>
    );
};

export default LeadSyncFormModal;
